/**
 * Geo-IP UI-language default for pre-auth screens (register / login).
 *
 * The app's i18n only detects language from localStorage + navigator.language,
 * so a visitor in Israel whose browser is English sees an English page. This
 * hook upgrades that English fallback to the geo-detected language (e.g. Hebrew
 * in Israel) using a public backend endpoint, WITHOUT overriding:
 *   - an explicit user choice (the LanguageSelector sets LANGUAGE_EXPLICIT_KEY), or
 *   - a browser that already provided a supported non-English language.
 *
 * It only ever "upgrades" the English fallback — it never downgrades a he/es UI
 * to English — so it stays a hint, weaker than both the user and the browser.
 */
import { useEffect } from 'react';

import { setupWizardApi } from '../shared/api/setupWizardApi';
import i18n, { SUPPORTED_LANGUAGES, changeLanguage, readKnownLanguageCookie } from './config';

/**
 * localStorage flag marking that the user explicitly picked a language via the
 * LanguageSelector. When set, geo-detection is suppressed so a deliberate choice
 * (e.g. English while sitting in Israel) is honored on every subsequent visit.
 */
export const LANGUAGE_EXPLICIT_KEY = 'panluma_language_explicit';

function isSupportedNonEnglish(lang: string | undefined): boolean {
  if (!lang) return false;
  const base = lang.split('-')[0];
  return base !== 'en' && SUPPORTED_LANGUAGES.some((l) => l.code === base);
}

/**
 * The language to persist as the durable account preference at signup, or
 * undefined when the user never explicitly chose one. Only an explicit
 * LanguageSelector choice (LANGUAGE_EXPLICIT_KEY) counts — the ambient UI
 * language may be a GeoIP guess and must never be baked into the account
 * (the backend then defaults the account to English). See
 * docs/platform/i18n-language-resolution-design.md.
 */
export function getExplicitLanguageChoice(): string | undefined {
  if (!localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) return undefined;
  const base = i18n.language?.split('-')[0];
  return base && SUPPORTED_LANGUAGES.some((l) => l.code === base) ? base : undefined;
}

export function useGeoLanguageDefault(): void {
  useEffect(() => {
    // Honor a known cross-subdomain preference — the account `preferred_language`
    // or an explicit LanguageSelector choice on any `*.panluma.ai` subdomain. Once
    // we know the user's language we never override it with a GeoIP guess, even to
    // upgrade the English fallback. (localStorage's LANGUAGE_EXPLICIT_KEY is
    // origin-scoped and so blind to a choice made on another subdomain.)
    if (readKnownLanguageCookie()) return;
    // Honor an explicit choice on this origin — never override it.
    if (localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) return;
    // Respect a browser that already gave a supported non-English language.
    if (isSupportedNonEnglish(i18n.language)) return;

    let cancelled = false;
    setupWizardApi
      .getPublicLocaleDefaults()
      .then(({ language }) => {
        if (cancelled) return;
        // Re-check the live state at resolve time: the visitor may have picked a
        // language via the LanguageSelector (explicit marker), signed in (known
        // cookie), or i18n may have settled on a supported language while the
        // request was in flight. Either way, don't stomp it — geo only ever
        // upgrades the English fallback.
        if (readKnownLanguageCookie()) return;
        if (localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) return;
        if (language !== i18n.language && isSupportedNonEnglish(language)) {
          void changeLanguage(language);
        }
      })
      .catch(() => {
        // Best-effort: a failed geo lookup just leaves the English fallback.
      });

    return () => {
      cancelled = true;
    };
  }, []);
}
